import { api } from '../api/client';

import { API_FAVORITES_URL, API_HOME_URL, API_ITEMS_NEWEST_URL } from '../utils';
import { normalizeId } from '../utils/ids';

// All mutations are idempotent on the API side (PUT to favorite, DELETE to
// unfavorite), so optimistic retries are safe.

export async function getFavorites() {
	const { data } = await api.get(API_FAVORITES_URL);
	return {
		collections: Array.isArray(data?.collections) ? data.collections : [],
		cards: Array.isArray(data?.cards) ? data.cards : [],
		// Every row, raw, in its section's order (keepr-api docs/SCHEMA.md §
		// User favorites). Absent on an API a deploy behind.
		pins: Array.isArray(data?.pins) ? data.pins : [],
	};
}

export function setCollectionFavorite(collectionId, favorite) {
	const url = `${API_FAVORITES_URL}/collection/${collectionId}`;
	return favorite ? api.put(url) : api.delete(url);
}

export function setCardFavorite(collectionId, cardId, favorite) {
	const url = `${API_FAVORITES_URL}/collection/${collectionId}/card/${cardId}`;
	return favorite ? api.put(url) : api.delete(url);
}

/**
 * The Home pin kinds (keepr-api docs/SCHEMA.md § Home pins) and the URL each
 * lives at. A record is named by its own id; a launcher inside the collection
 * it launches in; a tag page (T5) by the tag's own id, of any kind. The
 * charts' (A7, § Overview and Home): a chart on Home and a dashboard launcher,
 * each named inside its CONTEXT — the collection whose items it counts.
 * (A person's own Overview choice is a row of its own kind too, but it is
 * chosen through services/overview.js, never toggled as a pin.)
 */
export const PIN_KIND = {
	ITEM: 'item',
	QUICK_ADD: 'quickAdd',
	MULTI_ADD: 'multiAdd',
	ADD_ITEM: 'addItem',
	TAG: 'tag',
	CHART_HOME: 'chartHome',
	DASHBOARD: 'dashboard',
	// A person's own saved combined list (KPR-1021): belongs to no collection.
	COMBINED_LIST: 'combinedList',
	// The newest record of a card in a collection, optionally under one of its
	// saved filters (KPR-1126): named by the card, with `savedFilterId`.
	LATEST_RECORD: 'latestRecord',
	// A saved filter of a collection (KPR-1124): named inside its collection,
	// like a launcher. The row keeps only its id; Home reads the filter.
	SAVED_FILTER: 'savedFilter',
	// A tile of one to three values of a record (KPR-1127): a fixed record
	// (named by the item) or the newest record of a card (named as the
	// newest-record pin is). The row keeps the element names; Home reads the
	// values each time it loads.
	RECORD_TILE: 'recordTile',
	// A short list of a card's records (KPR-1128): named as the newest-record
	// pin is (the card, with `savedFilterId`), with `count`. The row keeps the
	// count; Home reads the records each time it loads.
	RECORD_LIST: 'recordList',
	// A collection or sub-collection pinned as a shortcut (KPR-1125): its own
	// row, never the collection favorite (the star) — target is the collection.
	COLLECTION_LINK: 'collectionLink',
};

/** Where a record tile's record comes from (KPR-1127). */
export const TILE_SOURCE = Object.freeze({ RECORD: 'record', LATEST: 'latest' });

/** The most values a tile shows (keepr-api utils/recordTiles.js MAX_ELEMENTS). */
export const TILE_MAX_ELEMENTS = 3;

/**
 * How many records a Home list shows (keepr-api utils/recordLists.js): the
 * person picks MIN to MAX; a new list shows DEFAULT.
 */
export const RECORD_LIST_COUNT = Object.freeze({ MIN: 3, MAX: 10, DEFAULT: 5 });

/** A chart on Home is small (a third of the row) or wide (two thirds) — ruled d18. */
export const CHART_PIN_SIZE = Object.freeze({ SMALL: 'small', WIDE: 'wide' });

const filterQuery = savedFilterId => (savedFilterId ? `?saved_filter_id=${encodeURIComponent(savedFilterId)}` : '');

export function pinUrl(kind, { targetId, collectionId, savedFilterId = null, source = null }) {
	switch (kind) {
		case PIN_KIND.ITEM: return `${API_FAVORITES_URL}/item/${targetId}`;
		case PIN_KIND.QUICK_ADD: return `${API_FAVORITES_URL}/collection/${collectionId}/quick-add/${targetId}`;
		case PIN_KIND.MULTI_ADD: return `${API_FAVORITES_URL}/collection/${collectionId}/multi-add/${targetId}`;
		case PIN_KIND.ADD_ITEM: return `${API_FAVORITES_URL}/collection/${collectionId}/add-item/${targetId}`;
		case PIN_KIND.TAG: return `${API_FAVORITES_URL}/tag/${targetId}`;
		case PIN_KIND.CHART_HOME: return `${API_FAVORITES_URL}/collection/${collectionId}/chart-home/${targetId}`;
		case PIN_KIND.DASHBOARD: return `${API_FAVORITES_URL}/collection/${collectionId}/dashboard/${targetId}`;
		case PIN_KIND.COMBINED_LIST: return `${API_FAVORITES_URL}/combined-list/${targetId}`;
		case PIN_KIND.LATEST_RECORD: return `${API_FAVORITES_URL}/collection/${collectionId}/latest-record/${targetId}${filterQuery(savedFilterId)}`;
		// A fixed record's tile is named by the record; the newest record's
		// tile by its collection, card and filter (targetId is the card).
		case PIN_KIND.RECORD_TILE: return source === TILE_SOURCE.LATEST
			? `${API_FAVORITES_URL}/collection/${collectionId}/latest-tile/${targetId}${filterQuery(savedFilterId)}`
			: `${API_FAVORITES_URL}/item/${targetId}/tile`;
		case PIN_KIND.RECORD_LIST: return `${API_FAVORITES_URL}/collection/${collectionId}/record-list/${targetId}${filterQuery(savedFilterId)}`;
		case PIN_KIND.SAVED_FILTER: return `${API_FAVORITES_URL}/collection/${collectionId}/saved-filter/${targetId}`;
		case PIN_KIND.COLLECTION_LINK: return `${API_FAVORITES_URL}/collection-link/${targetId}`;
		default: throw new Error(`Unknown pin kind: ${kind}`);
	}
}

/**
 * The `{ targetId, collectionId, savedFilterId? }` a resolved pin (GET
 * /api/home, GET /api/favorites) is pinned and unpinned by. A newest-record
 * pin and a list of records (KPR-1128) are named by their CARD and filter
 * (the row's `target_id` is the filter when it has one); every other kind by
 * its `target_id`.
 */
export function pinTargetOf(pin) {
	if (pin?.targetType === PIN_KIND.RECORD_TILE) {
		const latest = pin.source === TILE_SOURCE.LATEST;
		return {
			source: latest ? TILE_SOURCE.LATEST : TILE_SOURCE.RECORD,
			targetId: normalizeId(latest ? (pin.card_id || pin.card?._id) : pin.target_id),
			collectionId: normalizeId(pin.collection_id),
			savedFilterId: latest ? (normalizeId(pin.savedFilter_id || pin.savedFilter?._id) || null) : null,
		};
	}
	if (pin?.targetType === PIN_KIND.LATEST_RECORD || pin?.targetType === PIN_KIND.RECORD_LIST) {
		return {
			targetId: normalizeId(pin.card_id || pin.card?._id),
			collectionId: normalizeId(pin.collection_id),
			savedFilterId: normalizeId(pin.savedFilter_id || pin.savedFilter?._id) || null,
		};
	}
	return { targetId: normalizeId(pin?.target_id), collectionId: normalizeId(pin?.collection_id) };
}

/**
 * Pin (PUT) or unpin (DELETE). A pin answers `{ ok, _id }` — the row id (a
 * chart pin also its `size`, a record tile its `elements`). A chart pin may
 * say its size (`target.size`, small unless said); a record tile names the
 * elements it shows (`target.elements`, one to three — a second PUT changes
 * them); a list of records (KPR-1128) says how many it shows
 * (`target.count`, 3 to 10 — a second PUT changes it); no other kind sends a
 * body.
 */
export async function setPin(kind, target, pinned) {
	const url = pinUrl(kind, target);
	if (!pinned) {
		const { data } = await api.delete(url);
		return data || { ok: true };
	}
	let body;
	if (kind === PIN_KIND.CHART_HOME && target && target.size) body = { size: target.size };
	if (kind === PIN_KIND.RECORD_TILE) body = { elements: Array.isArray(target?.elements) ? target.elements : [] };
	if (kind === PIN_KIND.RECORD_LIST && Number.isInteger(target?.count)) body = { count: target.count };
	const { data } = body ? await api.put(url, body) : await api.put(url);
	return data || { ok: true };
}

/**
 * The newest record of a card the caller may read, NOW (KPR-1126): GET
 * /api/items/newest → `{ item: { _id, collection_id, card_id, displayValue }
 * | null, canAdd, addCollectionId }`. `canAdd` says whether the caller may
 * add an item of the card where it lives (`addCollectionId`).
 */
export async function getNewestItem({ collectionId, cardId, savedFilterId = null }) {
	const params = { collection_id: collectionId, card_id: cardId };
	if (savedFilterId) params.saved_filter_id = savedFilterId;
	const { data } = await api.get(API_ITEMS_NEWEST_URL, { params });
	return {
		item: data?.item && typeof data.item === 'object' ? data.item : null,
		canAdd: data?.canAdd === true,
		addCollectionId: normalizeId(data?.addCollectionId) || collectionId,
	};
}

/** A chart pin's size (PATCH, own pins only): answers `{ ok, _id, size }`. */
export async function setChartPinSize({ targetId, collectionId }, size) {
	const { data } = await api.patch(pinUrl(PIN_KIND.CHART_HOME, { targetId, collectionId }), { size });
	return data || { ok: true, size };
}

/**
 * Rewrites the caller's order within one section ('collections' | 'pinned'
 * | 'charts'). `pinned` is Home's ONE Pinned strip (KPR-1123): launchers,
 * records, tags, combined lists and saved filters in one order. `ids` are favorite ROW ids;
 * a partial list is fine — the rest keep their order after it.
 */
export async function setFavoriteOrder(section, ids) {
	const { data } = await api.put(`${API_FAVORITES_URL}/order`, { section, ids });
	return data;
}

/**
 * GET /api/home — pins resolved and access-filtered:
 * { pinned, launchers, records, tags, combinedLists, charts, collections,
 * collectionGroups, favoriteCards, personal, dropped }. `pinned` (KPR-1123)
 * is the ONE Pinned strip — every launcher, record, tag and combined list
 * pin in the person's one order — and the four arrays after it are derived
 * from it here (withPinned); the API answers `pinned` alone since KPR-1134. `charts` (A7) are charts pinned to Home:
 * each `{ _id, target_id, collection_id (its context), collection, size,
 * chart }` — `chart` as the chart page reads it there; a dashboard launcher
 * among `launchers` carries `dashboard` ({ _id, name, scope }). Home never runs a chart: the page runs them in one batch. `tags` are pinned tag pages (T5): each pin's `tag` is
 * the tag's wire shape (an item tag's with its item's `title`), `card` an item
 * tag's card, `collection` null where the caller cannot see the tag's
 * collection. `collectionGroups` is the caller's own private tags on
 * collections: `[{ tag, collections: [identity] }]`. Both are `[]` on an API a
 * deploy behind. Null on an API that predates Home (404), so a surface can go
 * without.
 */
/**
 * The pin kinds of Home's ONE Pinned strip, and which of the old arrays each
 * was in — launchers, records, tags, lists, the order the strip shows a
 * person's pins from before it (KPR-1123).
 */
export const PINNED_ARRAYS = {
	// A kind added after KPR-1123 (a saved filter, KPR-1124) is in `pinned`
	// alone: the API never put it in an old array, so none is derived here.
	launchers: [PIN_KIND.ADD_ITEM, PIN_KIND.QUICK_ADD, PIN_KIND.MULTI_ADD, PIN_KIND.DASHBOARD],
	records: [PIN_KIND.ITEM],
	tags: [PIN_KIND.TAG],
	combinedLists: [PIN_KIND.COMBINED_LIST],
};

/**
 * A Home read with `pinned` as given, and the per-kind arrays (`launchers`,
 * `records`, `tags`, `combinedLists`) DERIVED from it, in its order — so the
 * one list is the only thing anything changes, and every reader of an old
 * array (the rail's pinned tags, the palette) sees what the strip sees.
 */
export function withPinned(home, pinned) {
	const list = Array.isArray(pinned) ? pinned : [];
	const next = { ...home, pinned: list };
	Object.entries(PINNED_ARRAYS).forEach(([key, kinds]) => {
		next[key] = list.filter(pin => kinds.includes(pin?.targetType));
	});
	return next;
}

export async function getHome() {
	try {
		const { data } = await api.get(API_HOME_URL);
		// Home's ONE Pinned strip (KPR-1123): the API answers `pinned` alone
		// since KPR-1134, so there is no per-kind fallback to read.
		const pinned = Array.isArray(data?.pinned) ? data.pinned : [];
		return withPinned({
			// Absent on an API a deploy behind (A7).
			charts: Array.isArray(data?.charts) ? data.charts : [],
			collections: Array.isArray(data?.collections) ? data.collections : [],
			collectionGroups: Array.isArray(data?.collectionGroups) ? data.collectionGroups : [],
			favoriteCards: Array.isArray(data?.favoriteCards) ? data.favoriteCards : [],
			// The personal collection leads the Collections strip (it is never a
			// starred collection — the API refuses the star). Dropping it here
			// left the strip without it whatever the API answered.
			personal: data?.personal && typeof data.personal === 'object' ? data.personal : null,
			dropped: Number(data?.dropped) || 0,
		}, pinned);
	} catch (err) {
		if (err?.response?.status === 404) return null;
		throw err;
	}
}
